import { Bot, Sparkles, Mic, Scale } from "lucide-react";
import { cn } from "@/lib/utils";
import { aiLevelMeta, levelFromPercent, type AiLevel } from "@/lib/ai-authorship";

/**
 * Small pill badge showing how a track was made (human / hybrid / 50% AI / 75% AI / 100% AI)
 * with explicit percentage and Lucide icons for high visibility & truthful sync licensing metadata.
 */
export function AiAuthorshipBadge({
  level,
  percent,
  className,
  showPercent = true,
  compositionHuman,
  publishingAiPercent,
  recordingAiPercent,
}: {
  level?: string | null;
  percent?: number | null;
  className?: string;
  showPercent?: boolean;
  compositionHuman?: boolean;
  publishingAiPercent?: number | null;
  recordingAiPercent?: number | null;
}) {
  const resolved: AiLevel | null =
    (level as AiLevel) ||
    (typeof percent === "number" ? levelFromPercent(percent) : null);

  const meta = aiLevelMeta(level || resolved);
  if (!meta && typeof percent !== "number") return null;

  const pct = typeof percent === "number" ? percent : meta?.defaultPercent ?? 0;
  const isHuman = pct === 0 || level === "human";

  let tooltip = isHuman
    ? "100% Human-made · Performed and written by people"
    : `${pct}% AI`;

  if (typeof publishingAiPercent === 'number' && typeof recordingAiPercent === 'number') {
    tooltip += ` (Publishing: ${publishingAiPercent}% AI · Recording: ${recordingAiPercent}% AI)`;
  } else if (compositionHuman && pct > 0) {
    tooltip += " (Composition & Copyright 100% Human)";
  } else if (pct === 50) {
    tooltip += " · Human Music/Lyrics + AI Recording";
  } else if (pct === 75) {
    tooltip += " · AI Music or Lyrics";
  }

  const badgeStyle = isHuman
    ? "bg-emerald-600/20 text-emerald-400 border-emerald-500/40"
    : pct <= 25
    ? "bg-sky-600/20 text-sky-400 border-sky-500/40"
    : pct <= 50
    ? "bg-violet-600/25 text-violet-300 border-violet-500/50"
    : "bg-amber-500/25 text-amber-300 border-amber-500/50";

  return (
    <span
      className={cn(
        "inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-semibold leading-none border shrink-0 transition-colors",
        badgeStyle,
        className,
      )}
      title={tooltip}
    >
      {isHuman ? (
        <Mic className="h-3 w-3 text-emerald-400 shrink-0" />
      ) : compositionHuman || pct === 50 ? (
        <Scale className="h-3 w-3 text-violet-300 shrink-0" />
      ) : pct >= 75 ? (
        <Bot className="h-3 w-3 text-amber-400 shrink-0" />
      ) : (
        <Sparkles className="h-3 w-3 text-sky-300 shrink-0" />
      )}

      <span>
        {isHuman
          ? "Human"
          : showPercent
          ? `${pct}% AI`
          : meta?.short || `${pct}% AI`}
      </span>
    </span>
  );
}

